import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useEffectEvent, useRef, useState, useTransition } from 'react';
import { SignInPageParams } from './signInTypes.ts';
import { ToroAuthClient } from '@repo/backend-types/auth-api';
import { AUTH_OTP_LENGTH } from '@repo/common/config';

/**
 * Hook that manages a countdown timer for rate-limiting actions like resending OTP codes.
 * Returns the seconds remaining, whether the cooldown is active, and a function to start it.
 * @param [initialCooldown] - Optional initial cooldown value in seconds. If provided, the cooldown will start immediately.
 */
function useCooldown(t0) {
  const $ = _c(12);
  const initialCooldown = t0 === undefined ? 0 : t0;
  const [secondsRemaining, setSecondsRemaining] = useState(initialCooldown);
  const isOnCooldown = secondsRemaining > 0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = seconds => setSecondsRemaining(seconds);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const startCooldown = t1;
  const intervalRef = useRef(null);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      if (!intervalRef.current) {
        intervalRef.current = setInterval(() => setSecondsRemaining(_temp), 1000);
      }
    };
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const startTicking = useEffectEvent(t2);
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const stopTicking = useEffectEvent(t3);
  let t4;
  if ($[3] !== isOnCooldown || $[4] !== startTicking || $[5] !== stopTicking) {
    t4 = () => {
      if (isOnCooldown) {
        startTicking();
        return () => stopTicking();
      }
    };
    $[3] = isOnCooldown;
    $[4] = startTicking;
    $[5] = stopTicking;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== isOnCooldown) {
    t5 = [isOnCooldown];
    $[7] = isOnCooldown;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  if ($[9] !== isOnCooldown || $[10] !== secondsRemaining) {
    t6 = {
      secondsRemaining,
      isOnCooldown,
      startCooldown
    };
    $[9] = isOnCooldown;
    $[10] = secondsRemaining;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function _temp(prev) {
  return prev <= 0 ? 0 : prev - 1;
}
export default function OtpForm(params) {
  const $ = _c(2);
  let t0;
  if ($[0] !== params) {
    t0 = <div className="otp-form"><BaseOtpForm {...params} /></div>;
    $[0] = params;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function BaseOtpForm(params) {
  const $ = _c(26);
  const {
    authClient,
    onError
  } = params;
  const [email, setEmail] = useState("");
  const [isSent, setIsSent] = useState(false);
  const [error, setError] = useState(null);
  const [isPending, startTransition] = useTransition();
  let t0;
  if ($[0] !== email) {
    t0 = email.includes("@");
    $[0] = email;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const isValid = t0;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsSent(false);
      setEmail("");
    };
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const goBack = t1;
  if (isSent) {
    let t2;
    if ($[3] !== email || $[4] !== params) {
      t2 = <SubmitOtpForm {...params} email={email} goBack={goBack} />;
      $[3] = email;
      $[4] = params;
      $[5] = t2;
    } else {
      t2 = $[5];
    }
    return t2;
  }
  let t2;
  if ($[6] !== authClient || $[7] !== email || $[8] !== isPending || $[9] !== isValid || $[10] !== onError) {
    t2 = e => {
      e.preventDefault();
      if (!isValid || isPending) {
        return;
      }
      setError(null);
      startTransition(async () => {
        const result = await sendOtp(authClient, email);
        if (!result.success) {
          if (result.code === "INVALID_EMAIL") {
            setError("Please enter a valid email address.");
            return;
          }
          await onError?.(result.code);
          setError("Failed to send verification code. Please try again.");
          return;
        }
        setIsSent(true);
      });
    };
    $[6] = authClient;
    $[7] = email;
    $[8] = isPending;
    $[9] = isValid;
    $[10] = onError;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const handleSubmit = t2;
  let t3;
  if ($[12] !== error) {
    t3 = error && <div className="error-box">{error}</div>;
    $[12] = error;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = e_0 => setEmail(e_0.target.value);
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  let t5;
  if ($[15] !== email || $[16] !== isPending) {
    t5 = <input disabled={isPending} type="email" placeholder="user@example.com" value={email} onChange={t4} />;
    $[15] = email;
    $[16] = isPending;
    $[17] = t5;
  } else {
    t5 = $[17];
  }
  const t6 = isPending || !isValid;
  const t7 = isPending ? "Sending..." : "Submit";
  let t8;
  if ($[18] !== t6 || $[19] !== t7) {
    t8 = <button type="submit" disabled={t6}>{t7}</button>;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] !== handleSubmit || $[22] !== t3 || $[23] !== t5 || $[24] !== t8) {
    t9 = <form onSubmit={handleSubmit}>{t3}{t5}{t8}</form>;
    $[21] = handleSubmit;
    $[22] = t3;
    $[23] = t5;
    $[24] = t8;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  return t9;
}
const COOLDOWN_SECONDS = 30;
interface SubmitOtpFormParams extends SignInPageParams {
  email: string;
  goBack: () => void;
}
// Step 2: Form with OTP input to sign in
function SubmitOtpForm(params: SubmitOtpFormParams) {
  const {
    authClient,
    email,
    callbackUrl,
    onSuccess,
    onError,
    goBack
  } = params;
  const [otp, setOtp] = useState('');
  const isValid = otp.length === AUTH_OTP_LENGTH;
  const [error, setError] = useState<string | null>(null);
  const [isVerifying, startVerifying] = useTransition();
  const [isResending, startResending] = useTransition();
  const busy = isVerifying || isResending;

  // Start cooldown immediately (OTP was just sent)
  const {
    secondsRemaining,
    isOnCooldown,
    startCooldown
  } = useCooldown(COOLDOWN_SECONDS);
  const handleResend = async () => {
    if (isOnCooldown || busy) return;
    setError(null);
    setOtp('');
    startCooldown(COOLDOWN_SECONDS);
    startResending(async () => {
      const result = await sendOtp(authClient, email);
      if (!result.success) {
        setError('Failed to resend verification code. Please try again.');
      }
    });
  };
  const handleSubmit = async () => {
    if (otp.length !== AUTH_OTP_LENGTH) {
      setError(`Invalid code format. Expected a ${AUTH_OTP_LENGTH} digit code.`);
      return;
    }
    setError(null);
    startVerifying(async () => {
      try {
        const {
          error: error_0,
          data
        } = await authClient.signIn.emailOtp({
          email,
          otp,
          callbackURL: await callbackUrl
        });
        if (!error_0) {
          return onSuccess?.(data);
        }
        const code = error_0.code;
        if (onError && code) {
          onError(code);
        }

        // Display unexpected auth errors above OTP form
        console.error('OTP sign-in error:', error_0);
        setError('Unable to sign in. Verify your code or contact your account administrator.');
      } catch (err) {
        console.error('OTP sign-in error:', err);
        setError('Unable to sign in. Please try again.');
      }
    });
  };
  return <form onSubmit={e => {
    e.preventDefault();
    handleSubmit();
  }}>
      <p className="otp-message">
        A code has been sent to your email, <strong>{email}</strong>.
      </p>
      {error && <div className="error-box">{error}</div>}
      <input type="text" className="otp-input" value={otp} placeholder={'Enter code'} minLength={AUTH_OTP_LENGTH} maxLength={AUTH_OTP_LENGTH} inputMode="numeric" pattern="[0-9]*" onChange={e_0 => setOtp(e_0.target.value.replace(/\D/g, ''))} />
      <button type="submit" disabled={!isValid}>
        {isVerifying ? 'Verifying...' : 'Submit'}
      </button>
      <div className="resend-text">
        {"Didn't receive a code? "}
        {isOnCooldown ? <span className="resend-cooldown">Resend in {secondsRemaining}s</span> : isResending ? <span className="resend-cooldown">Resending…</span> : <a className="resend-link" onClick={handleResend}>
            Resend
          </a>}
      </div>
      <div className="resend-text">
        {'Wrong email? '}
        <a className="resend-link" onClick={goBack}>
          Go back
        </a>
      </div>
    </form>;
}
async function sendOtp(authClient: ToroAuthClient, email: string) {
  console.log('Sending OTP email to ' + email);
  try {
    const {
      data,
      error
    } = await authClient.emailOtp.sendVerificationOtp({
      email,
      type: 'sign-in'
    });
    if (error) {
      console.warn('sendOtp error:', error);
      return {
        success: false as const,
        code: error.code,
        message: error.message
      };
    }
    console.log('OTP sent', data);
    return {
      success: true as const
    };
  } catch (err) {
    console.error('sendOtp error:', err);
    return {
      success: false as const,
      message: err instanceof Error ? err.message : String(err)
    };
  }
}